import { type FC, useContext, useLayoutEffect, useState } from 'react';

import { TutorialContext } from '@/components/Tutorial/context';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import { selectProductOfDayById } from '@/redux/selectors/contractSelectors';
import { useAppSelector } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import ProductOfDayDefaultScreen from './screens/ProductOfDayDefaultScreen/ProductOfDayDefaultScreen';
import UpgradeScreen from './screens/UpgradeScreen/UpgradeScreen';
import { Timer } from './Timer';

import gradientStarsPng from '/img/modals/gradient-stars.png';
import gradientStarsWebp from '/img/modals/gradient-stars.webp';
import gradientStarsPurplePng from '/img/modals/gradient-stars-purple.png';
import gradientStarsPurpleWebp from '/img/modals/gradient-stars-purple.webp';

interface ProductOfDayModalProps {
  productId: string;
  initialScreen?: 'default' | 'upgrade' | 'rewards';
}

const ProductOfDayModal: FC<ProductOfDayModalProps> = ({
  productId,
  initialScreen = 'default',
}) => {
  const { closeModal, thirdModal } = useContext(ModalContext);
  const [screen, setScreen] = useState<'default' | 'upgrade' | 'rewards'>(
    initialScreen,
  );
  const product = useAppSelector((state) =>
    selectProductOfDayById(state, productId),
  );
  const { setActiveDialog } = useContext(TutorialContext);
  const userLevel = useAppSelector((state) => state.user.user?.level);

  useLayoutEffect(() => {
    if (!localStorage.getItem('product-of-day-tutorial') && userLevel === 2) {
      setActiveDialog('product-of-day-tutorial');
      localStorage.setItem('product-of-day-tutorial', 'shown');
    }
  }, [userLevel]);

  if (!product) {
    return null;
  }

  const handleClose = (): void => {
    if (
      thirdModal.isOpen &&
      thirdModal.modalContent.renderKind === 'productOfDayModal'
    ) {
      thirdModal.closeModal();
    } else {
      closeModal();
    }
  };

  return (
    <div className="relative w-full z-0 pt-5 max-h-[42.75rem] flex flex-col pb-10">
      <Timer screen={screen} expiredAt={product.expiredAt} />
      <Close
        onClick={handleClose}
        className="absolute top-4 right-4"
        imageVariant="outline"
      />
      <Picture
        className="absolute w-full -z-10 top-0 left-0"
        img={
          product.type === 'premium' || screen === 'upgrade'
            ? gradientStarsPurplePng
            : gradientStarsPng
        }
        webp={
          product.type === 'premium' || screen === 'upgrade'
            ? gradientStarsPurpleWebp
            : gradientStarsWebp
        }
        alt="gradient stars"
      />
      {screen === 'default' && (
        <ProductOfDayDefaultScreen
          onUpgrade={() => {
            if (product.type !== 'premium') {
              setScreen('upgrade');
            } else {
              setScreen('rewards');
            }
          }}
          product={product}
        />
      )}
      {screen === 'upgrade' && (
        <UpgradeScreen
          onCancel={() => setScreen('default')}
          product={product}
          type="upgrade"
        />
      )}
      {screen === 'rewards' && (
        <UpgradeScreen
          onCancel={() => setScreen('default')}
          product={product}
          type="rewards"
        />
      )}
    </div>
  );
};

export default ProductOfDayModal;
